import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getBrandShopName } from '@utils/get-brand-name.ts';

import { useModals } from '@shared/contexts';

import { useMutationAttachTradeLink } from '@hooks/api/user/use-mutation-attach-trade-link.ts';
import { useMutationPassOnboarding } from '@hooks/api/user/use-mutation-pass-onboarding.ts';

import { Button } from '@components/button';
import { Input } from '@components/input';
import { ModalName } from '@components/modals/types.ts';

import * as Styled from './trade-link.styled.ts';

type OwnProps = {
	onClickPrevious: () => void;
};

const TradeLink: React.FC<OwnProps> = ({ onClickPrevious }) => {
	const { t } = useTranslation(['main', 'welcome', 'notifications']);
	const { mutate: markOnboardingPassed } = useMutationPassOnboarding();
	const { mutate: attachTradeLink, isSuccess, isError, isPending } = useMutationAttachTradeLink();
	const { openModal } = useModals();

	const [link, setLink] = React.useState('');
	const [isValid, setIsValid] = React.useState(true);

	const navigate = useNavigate();

	const handleTradeClick = () => {
		openModal(ModalName.TRADE_LINK_HELP);
	};

	const handleChange = (e: any) => {
		const value = e.target.value;
		setLink(value);

		const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
		setIsValid(urlRegex.test(value));
	};

	const attachTradeLinkHandler = async (link: string) => {
		if (!link) {
			setIsValid(false);
			return;
		}

		attachTradeLink({ steam_trade_url: link });
	};

	React.useEffect(() => {
		if (isError) {
			setIsValid(false);
			toast.error(t('notifications:wrong_trade_link'), {
				hideProgressBar: true
			});
		}

		if (isSuccess) {
			toast.success(t('notifications:trade_link_attached'), {
				hideProgressBar: true
			});
		}
	}, [isError, isSuccess, t]);

	const Enter = () => {
		if (!isValid) {
			toast.error(t('notifications:wrong_trade_link'), {
				hideProgressBar: true
			});

			return;
		}

		attachTradeLinkHandler(link);
	};

	const handleMarkOnboardingPassed = () => {
		markOnboardingPassed();
		navigate('/');
	};

	const shopName = getBrandShopName();

	return (
		<Styled.Wrapper>
			<Styled.ModalWrapper>
				<Styled.Header>
					<Button variant={'round'} onClick={onClickPrevious} hasIcon iconPosition='start' />
					<Styled.Title>{t('trade_link')}</Styled.Title>
				</Styled.Header>
				<Styled.Content>
					<Styled.Info>
						{t('welcome:trade_link_text1', { shopName })}
					</Styled.Info>
					<Styled.InputWrapper>
						<Input label={t('link')} onChange={handleChange} type='text' value={link} placeholder='https://' />
					</Styled.InputWrapper>
					<Styled.PreFooter>
						<Button color='ghost' onClick={handleTradeClick}>
							{t('welcome:trade_link_text2')}
						</Button>
						<Styled.ButtonsGroup>
							<NavLink to='/' onClick={handleMarkOnboardingPassed}>
								<Button color='dark'>{t('skip')}</Button>
							</NavLink>
							<Button onClick={Enter} color='primary' disabled={isPending}>
								{t('attach')}
							</Button>
						</Styled.ButtonsGroup>
					</Styled.PreFooter>

					<Styled.Footer>{t('welcome:skip_step')}</Styled.Footer>
				</Styled.Content>
			</Styled.ModalWrapper>
		</Styled.Wrapper>
	);
};

export default TradeLink;
